import { useState } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'

import StyledTable from '@components/StyledTable'
import { ToggleSetButton } from '@components/ToggleSetButton'
import type { Entity } from '@models/Entity'

import type { AuthenticatedUser } from '../../../auth/types/AuthenticatedUser'
import type { BijlageTemplate } from '../../../templates/types/BijlageTemplate'
import { PageSection } from '../../../theme/components/PageSection'
import { useOpenBijlageForEntity } from '../hooks/useOpenBijlageForEntity'
import type { Bijlage } from '../types/Bijlage'
import type { BijlagenDialogType } from '../types/BijlagenDialogType'

import { AlgemeneBijlage } from './AlgemeneBijlage'
import { BijlagenDialog } from './BijlagenDialog'

const headerCells = [
  { id: 'documentsoort', label: 'Documentsoort', sort: false },
  { id: 'bijlagen', label: 'Bestanden', sort: false, fixed: 200 },
  { id: 'opties', label: 'Opties', sort: false, fixed: 120 },
] as const

interface Props {
  entity: Entity
  bijlagen: Bijlage[]
  bijlageTemplates: BijlageTemplate[]
  user: AuthenticatedUser
}

const notCancelledFilter = (x) => x.status !== 'cancelled'
const cancelledFilter = (x) => x.status === 'cancelled'

export const AlgemeneBijlagen = ({
  entity,
  bijlagen,
  bijlageTemplates,
  user,
}: Props) => {
  const { mutate: openBijlageForEntity } = useOpenBijlageForEntity()
  const [type, setType] = useState<BijlagenDialogType>()
  const [selectedBijlage, setSelectedBijlage] = useState<Bijlage | undefined>()
  const [selectedDocumentsoort, setSelectedDocumentsoort] = useState<string>('')
  const [showCancelled, setShowCancelled] = useState(false)

  const algemeneBijlageTemplates = bijlageTemplates.filter(
    (template) => template.scope === 'algemeen',
  )

  const algemeneBijlagen = bijlagen.filter((bijlage) => !bijlage.parentId)

  const handleOpen = (bijlage: Bijlage) => {
    openBijlageForEntity({ entity, bijlage })
  }

  const handleUpload = (documentsoort: string) => {
    setSelectedDocumentsoort(documentsoort)
    setType('upload')
  }

  const handleCancel = (bijlage: Bijlage) => {
    setSelectedBijlage(bijlage)
    setType('cancel')
  }

  const handleClose = () => {
    setType(undefined)
    setSelectedBijlage(undefined)
    setSelectedDocumentsoort('')
  }

  return (
    <PageSection
      heading="Algemene bijlagen"
      id="algemene-bijlagen"
      actions={
        <ToggleSetButton
          active={showCancelled}
          onClick={() => setShowCancelled(!showCancelled)}
          toggledSetSize={algemeneBijlagen.filter(cancelledFilter).length}
          type="bijlagen"
        />
      }
    >
      <Box>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              {showCancelled ? 'Alle bijlagen' : 'Actieve bijlagen'}
            </Typography>
          </AccordionSummary>

          <Toolbar
            sx={{
              justifyContent: 'flex-end',
              backgroundColor: '#F7F7F7',
              borderBottom: '1px solid #E0E0E0',
            }}
          >
            <Box sx={{ p: 1, textAlign: 'right' }}></Box>
          </Toolbar>
          <StyledTable
            variant="details"
            headerCells={headerCells}
            {...({} as any)}
          >
            {algemeneBijlageTemplates.map((template) => (
              <AlgemeneBijlage
                key={template.documentsoort}
                entity={entity}
                template={template}
                bijlagen={algemeneBijlagen.filter(
                  showCancelled ? () => true : notCancelledFilter,
                )}
                user={user}
                onOpen={handleOpen}
                onUpload={handleUpload}
                onCancel={handleCancel}
              />
            ))}
          </StyledTable>
        </Accordion>

        <BijlagenDialog
          type={type}
          bijlage={selectedBijlage}
          entity={entity}
          assignees={[]}
          bijlageTemplates={algemeneBijlageTemplates}
          disciplineVerzamelingen={[]}
          processtatus=""
          documentsoort={selectedDocumentsoort}
          onClose={handleClose}
        />
      </Box>
    </PageSection>
  )
}
